/**
 * LIVE screen UI state (per open mission, in memory): HUD focus aircraft + reference, the
 * right-panel / bottom-sheet tab, the geometry pair drawn on the map, and the MARK label sheet.
 */
import { create } from 'zustand';
import type { Id, TargetRef } from '@apos/shared';
import type { SheetSnap } from '../../components/ui';

export type LiveTab = 'picture' | 'plan' | 'markers' | 'weather' | 'sun' | 'alerts' | 'calls';

export interface LiveUiState {
  missionId: Id | null;
  /** null = automatic (me → my camera → camera site → airport) */
  reference: TargetRef | null;
  /** null = first subject aircraft */
  focusId: Id | null;
  tab: LiveTab;
  panelOpen: boolean;
  sheet: SheetSnap;
  /** Extra geometry pair picked from the relative-geometry table. */
  pair: { from: TargetRef; to: TargetRef } | null;
  /** Marker currently open in the quick label sheet. */
  labelMarkerId: Id | null;

  ensureMission(id: Id | null): void;
  setReference(r: TargetRef | null): void;
  setFocus(id: Id | null): void;
  setTab(t: LiveTab): void;
  /** Show a tab: opens the desktop panel / raises the phone sheet. */
  openTab(t: LiveTab): void;
  setPanelOpen(v: boolean): void;
  setSheet(s: SheetSnap): void;
  setPair(p: LiveUiState['pair']): void;
  openLabel(markerId: Id | null): void;
}

const initial = { reference: null, focusId: null, tab: 'picture' as LiveTab, panelOpen: true, sheet: 'peek' as SheetSnap, pair: null, labelMarkerId: null };

export const useLiveUi = create<LiveUiState>((set, get) => ({
  missionId: null,
  ...initial,
  ensureMission(id) {
    if (get().missionId !== id) set({ missionId: id, ...initial, panelOpen: get().panelOpen });
  },
  setReference: (reference) => set({ reference }),
  setFocus: (focusId) => set({ focusId }),
  setTab: (tab) => set({ tab }),
  openTab: (tab) => set((s) => ({ tab, panelOpen: true, sheet: s.sheet === 'peek' ? 'half' : s.sheet })),
  setPanelOpen: (panelOpen) => set({ panelOpen }),
  setSheet: (sheet) => set({ sheet }),
  setPair: (pair) => set({ pair }),
  openLabel: (labelMarkerId) => set({ labelMarkerId }),
}));
